import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useChurch, useChurchManagedEvents, useChurchDrafts } from '../hooks/useChurches'
import { useChurchPlan } from '../hooks/useChurchAdmin'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn, Segmented } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import { getEventType } from '../components/ui/eventTypes'
import { BulletinImportModal } from '../components/admin/BulletinImportModal'
import { SubscriptionRequiredNotice } from '../components/admin/SubscriptionRequiredNotice'
import type { OccurrenceWithEvent, FellowshipEvent } from '@fellowship/shared'

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function formatDateBlock(iso: string) {
  const d = new Date(iso)
  return {
    dow: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
    day: String(d.getDate()),
    mon: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
  }
}

function formatShortDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

type GroupedOccurrence = { next: OccurrenceWithEvent; moreCount: number }

function groupByEvent(occs: OccurrenceWithEvent[]): GroupedOccurrence[] {
  const map = new Map<string, GroupedOccurrence>()
  for (const occ of occs) {
    const key = occ.event.id
    if (!map.has(key)) map.set(key, { next: occ, moreCount: 0 })
    else map.get(key)!.moreCount++
  }
  return Array.from(map.values())
}

function EventRow({ occ, first, slug, moreCount = 0 }: { occ: OccurrenceWithEvent; first: boolean; slug: string; moreCount?: number }) {
  const t = getEventType(occ.event.eventType)
  const db = formatDateBlock(occ.startsAt)
  const navigate = useNavigate()

  const progress = occ.dishProgress
  const totalGoal = progress?.goals
    ? Object.values(progress.goals as Record<string, number>).reduce((a, b) => a + b, 0)
    : 0
  const totalClaimed = progress?.claimed
    ? Object.values(progress.claimed as Record<string, number>).reduce((a, b) => a + b, 0)
    : 0
  const ratio = totalGoal > 0 ? totalClaimed / totalGoal : null

  return (
    <div className={`flex items-center gap-4 px-5 py-4 ${first ? '' : 'border-t border-line-soft'}`}>
      <div className="w-[46px] text-center shrink-0">
        <div className="mono text-[9.5px] text-muted tracking-[0.08em]">{db.dow}</div>
        <div className="serif text-[22px] leading-none tracking-[-0.02em]">{db.day}</div>
        <div className="mono text-[9px] text-muted mt-0.5">{db.mon}</div>
      </div>

      <div
        className="w-8 h-8 rounded-lg shrink-0 inline-flex items-center justify-center"
        style={{ background: t.soft, color: t.color }}
      >
        <t.icon size={15} strokeWidth={1.9} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Link
            to={`/churches/${slug}/occurrences/${occ.id}/overview`}
            className="text-[14.5px] font-medium truncate text-ink no-underline hover:underline"
          >
            {occ.event.title}
          </Link>
          {occ.event.isRecurring && (
            <span className="text-[9.5px] px-[7px] py-0.5 rounded bg-s2 text-muted font-mono tracking-[0.08em] shrink-0">
              RECURRING{moreCount > 0 ? ` · +${moreCount} more` : ''}
            </span>
          )}
          {occ.isCancelled && (
            <span className="text-[9.5px] px-[7px] py-0.5 rounded bg-danger-soft text-danger-deep font-mono tracking-[0.08em] shrink-0">CANCELLED</span>
          )}
        </div>
        <div className="mt-[3px] text-[11.5px] text-muted flex gap-2">
          <span>{formatTime(occ.startsAt)}</span>
          {(occ.overrideAddressText ?? occ.event.addressText) && (
            <><span>·</span><span className="truncate">{occ.overrideAddressText ?? occ.event.addressText}</span></>
          )}
        </div>
      </div>

      {ratio !== null ? (
        <div className="w-[100px] shrink-0">
          <div className={`mono text-[9.5px] mb-[3px] text-right ${ratio < 0.5 ? 'text-[#A33A2A]' : 'text-muted'}`}>
            {totalClaimed}/{totalGoal} dishes
          </div>
          <div className="h-1.5 rounded-[3px] bg-s2 border border-line-soft relative">
            <div
              className="absolute inset-0 rounded-[3px]"
              style={{
                right: `${(1 - ratio) * 100}%`,
                background: ratio >= 0.85 ? 'var(--good)' : ratio < 0.5 ? '#A33A2A' : 'var(--warn)',
              }}
            />
          </div>
        </div>
      ) : <div className="w-[100px]" />}

      <div className="w-[52px] text-center shrink-0">
        <div className="text-[16px] font-semibold">{occ.rsvpCount ?? 0}</div>
        <div className="mono text-[9px] text-muted">RSVPS</div>
      </div>

      <div className="flex gap-1.5 shrink-0">
        <Link to={`/events/${occ.event.id}`} target="_blank" className="no-underline">
          <AdmBtn size="sm" variant="ghost">View ↗</AdmBtn>
        </Link>
        <Link to={`/churches/${slug}/events/${occ.event.id}/edit`} className="no-underline">
          <AdmBtn size="sm" variant="ghost">Edit</AdmBtn>
        </Link>
        <AdmBtn size="sm" variant="ghost" onClick={() => navigate(`/churches/${slug}/occurrences/${occ.id}/overview`)}>
          Manage <AdmIcn name="chev" size={11} />
        </AdmBtn>
      </div>
    </div>
  )
}

function DraftRow({ draft, first, slug }: { draft: FellowshipEvent; first: boolean; slug: string }) {
  const t = getEventType(draft.eventType)
  return (
    <div className={`flex items-center gap-4 px-5 py-4 ${first ? '' : 'border-t border-line-soft'}`}>
      <div className="w-[46px] shrink-0" />

      <div
        className="w-8 h-8 rounded-lg shrink-0 inline-flex items-center justify-center"
        style={{ background: t.soft, color: t.color }}
      >
        <t.icon size={15} strokeWidth={1.9} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-[14.5px] font-medium truncate">{draft.title}</div>
          <span className="text-[9.5px] px-[7px] py-0.5 rounded bg-ochre-soft text-ochre font-mono tracking-[0.08em] shrink-0">DRAFT</span>
        </div>
        <div className="mt-[3px] text-[11.5px] text-muted">
          Added {formatShortDate(draft.createdAt)}
        </div>
      </div>

      <div className="w-[100px] shrink-0" />
      <div className="w-[52px] shrink-0" />

      <div className="shrink-0">
        <Link to={`/churches/${slug}/events/${draft.id}/edit`} className="no-underline">
          <AdmBtn size="sm" variant="primary">Edit & publish →</AdmBtn>
        </Link>
      </div>
    </div>
  )
}

export default function ChurchEventsPage() {
  const { slug } = useParams<{ slug: string }>()
  const [tab, setTab] = useState('Upcoming')
  const [showBulletinImport, setShowBulletinImport] = useState(false)

  const { data: churchData, isLoading: churchLoading } = useChurch(slug)
  const church = churchData?.data
  const { data: eventsData, isLoading: eventsLoading } = useChurchManagedEvents(slug)
  const { data: draftsData, isLoading: draftsLoading } = useChurchDrafts(church?.id)
  const plan = useChurchPlan(church?.id)

  const allOccurrences = eventsData?.data ?? []
  const drafts = draftsData?.data ?? []
  const now = new Date()

  const upcoming = allOccurrences.filter((o) => new Date(o.startsAt) >= now)
  const past = allOccurrences.filter((o) => new Date(o.startsAt) < now).reverse()
  const groupedUpcoming = groupByEvent(upcoming)
  const groupedPast = groupByEvent(past)

  // Free-plan upsell — shown once we know the church is on the Free floor. Not a
  // wall: a Free church can still post its one event; this just nudges upgrading.
  const showFreePlanUpsell = !plan.loading && plan.isFree

  const tabOptions = drafts.length > 0
    ? ['Upcoming', 'Past', `Drafts${drafts.length > 0 ? ` (${drafts.length})` : ''}`]
    : ['Upcoming', 'Past']

  const activeTab = tab.startsWith('Drafts') ? 'Drafts' : tab

  const subParts = [`${groupedUpcoming.length} upcoming`, `${groupedPast.length} past`]
  if (drafts.length > 0) subParts.push(`${drafts.length} draft${drafts.length !== 1 ? 's' : ''}`)

  if (churchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Loading…</div>
      </div>
    )
  }

  if (!church) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Church not found.</div>
      </div>
    )
  }

  const isLoading = eventsLoading || draftsLoading
  const currentCount = activeTab === 'Upcoming' ? groupedUpcoming.length : activeTab === 'Past' ? groupedPast.length : drafts.length

  return (
    <AdminShell>
      <AdminPageHeader
        eyebrow={`${church.name} · events`}
        title="Events"
        sub={subParts.join(' · ')}
        actions={
          <>
            {/* Bulletin import is a Community+ capability */}
            {plan.canBroadcast && (
              <AdmBtn icon="doc" variant="ghost" onClick={() => setShowBulletinImport(true)}>
                Import bulletin
              </AdmBtn>
            )}
            {/* Any approved church can post — Free plan is capped, not blocked */}
            <Link to={`/churches/${slug}/events/new`} className="no-underline">
              <AdmBtn icon="plus" variant="primary">New event</AdmBtn>
            </Link>
          </>
        }
      />

      {showFreePlanUpsell && (
        <SubscriptionRequiredNotice
          slug={slug!}
          title="You're on the Free plan"
          message="You can keep one upcoming event posted for free. Upgrade for unlimited events, congregation broadcasts, and more."
          cta={plan.trialAvailable ? 'Start free trial' : 'See plans'}
          className="mb-5"
        />
      )}

      <AdminCard padding={0} style={{ paddingBottom: 6 }}>
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="serif text-[20px]">
            {currentCount} {activeTab.toLowerCase()} event{currentCount !== 1 ? 's' : ''}
          </div>
          <Segmented value={tab} options={tabOptions} onChange={setTab} />
        </div>

        {isLoading && (
          <div className="px-5 py-8 text-[13px] text-muted">Loading…</div>
        )}

        {!isLoading && activeTab !== 'Drafts' && (activeTab === 'Upcoming' ? groupedUpcoming : groupedPast).length === 0 && (
          <div className="px-5 py-12 text-center">
            <div className="text-[13px] text-muted mb-4">
              {activeTab === 'Upcoming' ? 'No upcoming events.' : 'No past events.'}
            </div>
            {activeTab === 'Upcoming' && (
              <Link to={`/churches/${slug}/events/new`} className="no-underline">
                <AdmBtn variant="primary" icon="plus">Create your first event</AdmBtn>
              </Link>
            )}
          </div>
        )}

        {!isLoading && activeTab === 'Drafts' && drafts.length === 0 && (
          <div className="px-5 py-12 text-center">
            <div className="text-[13px] text-muted">No drafts.</div>
          </div>
        )}

        {!isLoading && activeTab !== 'Drafts' && (activeTab === 'Upcoming' ? groupedUpcoming : groupedPast).map(({ next, moreCount }, i) => (
          <EventRow key={next.event.id} occ={next} first={i === 0} slug={slug!} moreCount={moreCount} />
        ))}

        {!isLoading && activeTab === 'Drafts' && drafts.map((draft, i) => (
          <DraftRow key={draft.id} draft={draft} first={i === 0} slug={slug!} />
        ))}
      </AdminCard>

      {showBulletinImport && church && (
        <BulletinImportModal
          churchId={church.id}
          churchSlug={slug ?? ''}
          churchAddressText={church.addressLine1 ?? ''}
          churchLocationLat={church.locationLat ?? 0}
          churchLocationLng={church.locationLng ?? 0}
          onClose={() => setShowBulletinImport(false)}
        />
      )}
    </AdminShell>
  )
}
